import type { GridView, Tone } from '../types';
import { CANVAS, GEO, TONES, toneColor, toneFill } from '../theme';

import { Caption, EmptyCanvas, FONT, LAYOUT, fmt, frameProps, valueWidth } from './shared';

const CELL = GEO.cell;
const GAP = 6;
const PAD = GEO.padding;
const ROW_GUTTER = 24;
const COL_GUTTER = 20;
/** Horizontal breathing room kept on each side of a cell's text. */
const CELL_TEXT_PAD = 16;

export function GridRenderer({ view }: { view: GridView }) {
  const rows = view.values.length;
  // Ragged rows are tolerated: the frame is sized to the widest row and the
  // missing tail cells are simply not drawn.
  const cols = Math.max(0, ...view.values.map((row) => row.length));
  const title = view.caption ?? (rows === 0 || cols === 0 ? 'Empty grid' : `Grid of ${rows} by ${cols}`);

  if (rows === 0 || cols === 0) return <EmptyCanvas title={title} />;

  const markKey = (r: number, c: number) => `${r}:${c}`;
  const marks = new Map<string, { tone: Tone; label?: string }>();
  for (const mark of view.marks ?? []) {
    if (mark.row >= 0 && mark.row < rows && mark.col >= 0 && mark.col < cols) {
      marks.set(markKey(mark.row, mark.col), { tone: mark.tone, label: mark.label });
    }
  }

  const cursor =
    view.cursor && view.cursor.row >= 0 && view.cursor.row < rows && view.cursor.col >= 0 && view.cursor.col < cols
      ? view.cursor
      : null;

  const captionY = view.caption ? PAD + 8 : 0;
  const gridTop = (view.caption ? PAD + LAYOUT.captionBand : PAD) + COL_GUTTER;
  const gridLeft = PAD + ROW_GUTTER;

  /*
   * A cell's value is drawn centred on one line — a value wider than the
   * base square cell (a column name, a multi-word string) would otherwise
   * overflow into the cell beside it. Widen every cell uniformly to fit the
   * widest value in the grid; row height stays fixed, only the column pitch
   * grows. Short DSA-style values (single numbers/short strings) never
   * exceed the base cell width, so this is a no-op for existing content.
   */
  const CW = Math.max(CELL, Math.max(0, ...view.values.flat().map((v) => valueWidth(fmt(v)))) + CELL_TEXT_PAD);

  const x = (c: number) => gridLeft + c * (CW + GAP);
  const y = (r: number) => gridTop + r * (CELL + GAP);

  const width = gridLeft + cols * CW + (cols - 1) * GAP + PAD;
  const height = gridTop + rows * CELL + (rows - 1) * GAP + PAD;

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      <g aria-hidden="true">
        {Array.from({ length: cols }, (_, c) => (
          <text
            key={`col-${c}`}
            x={x(c) + CW / 2}
            y={gridTop - COL_GUTTER / 2}
            fill={CANVAS.textFaint}
            fontSize={FONT.index}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {c}
          </text>
        ))}
        {Array.from({ length: rows }, (_, r) => (
          <text
            key={`row-${r}`}
            x={gridLeft - ROW_GUTTER / 2}
            y={y(r) + CELL / 2}
            fill={CANVAS.textFaint}
            fontSize={FONT.index}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {r}
          </text>
        ))}
      </g>

      {view.values.map((row, r) =>
        row.map((value, c) => {
          const mark = marks.get(markKey(r, c));
          return (
            <g key={`cell-${r}-${c}`}>
              <rect
                x={x(c)}
                y={y(r)}
                width={CW}
                height={CELL}
                rx={6}
                fill={mark ? toneFill(mark.tone) : CANVAS.cell}
                stroke={mark ? toneColor(mark.tone) : CANVAS.cellBorder}
                strokeWidth={1.5}
              />
              <text
                x={x(c) + CW / 2}
                y={y(r) + CELL / 2}
                fill={CANVAS.text}
                fontSize={FONT.value}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {fmt(value)}
              </text>
              {mark?.label ? (
                <text
                  x={x(c) + CW - 4}
                  y={y(r) + 9}
                  fill={toneColor(mark.tone)}
                  fontSize={FONT.label}
                  textAnchor="end"
                  dominantBaseline="central"
                >
                  {mark.label}
                </text>
              ) : null}
            </g>
          );
        }),
      )}

      {cursor ? (
        <g>
          <rect
            x={x(cursor.col) - 3}
            y={y(cursor.row) - 3}
            width={CW + 6}
            height={CELL + 6}
            rx={8}
            fill="none"
            stroke={TONES.accent}
            strokeWidth={3}
            aria-hidden="true"
          />
          {cursor.label ? (
            <text
              x={x(cursor.col) + CW / 2}
              y={y(cursor.row) + CELL + 12}
              fill={TONES.accent}
              fontSize={FONT.label}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {cursor.label}
            </text>
          ) : null}
        </g>
      ) : null}
    </svg>
  );
}

export default GridRenderer;
